import { PipetteIcon, RotateCcwIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { SketchPicker } from "react-color";
import { useTranslation } from "react-i18next";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

interface Props {
  value?: string | null;
  defaultColor?: string | null | false;
  disabled?: boolean;
  onChange(value: string);
}

const swatches = [
  "#54BAB9",
  "#9ED2C6",
  "#E9DAC1",
  "#850E35",
  "#DB4764",
  "#554994",
  "#F675A8",
  "#277BC0",
  "#FFB200",
  "#8728CA",
  "#6548C7",
  "#2D4494",
  "#3C4C8A",
  "#2898CF",
  "#16A795",
  "#16A74A",
  "#6DA80D",
  "#D4710C",
  "#D44E0C",
  "#CA1D13",
];

const ColorPicker = ({
  value,
  onChange,
  defaultColor = null,
  disabled,
}: Props) => {
  const { t } = useTranslation();
  const [curColor, setCurColor] = useState(value);
  const [eyedropperAvailable, setEyedropperAvailable] = useState(false);

  const _onDefault = () => {
    if (disabled) return;
    if (defaultColor === false) {
    } else if (defaultColor || defaultColor === null) {
      onChange(defaultColor as string);
    }
  };

  useEffect(() => {
    setCurColor(value);
  }, [value]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      if (typeof (window as any).EyeDropper !== "undefined") {
        setEyedropperAvailable(true);
      }
    }
  }, []);

  const onGetDropper = () => {
    if (typeof window !== "undefined") {
      const eyeDropper = new (window as any).EyeDropper();
      eyeDropper
        .open()
        .then((result) => {
          setCurColor(result.sRGBHex);
          onChange(result.sRGBHex);
        })
        .catch(() => {
          //ok
        });
    }
  };

  return (
    <Popover>
      <div className="flex items-center">
        <div className="flex items-center">
          <PopoverTrigger asChild>
            <button disabled={disabled}>
              <div
                className="rounded-md border bg-background size-8"
                style={{
                  backgroundColor: `${value || "#fff"}`,
                }}
              />
            </button>
          </PopoverTrigger>
          {eyedropperAvailable && (
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <button
                    className="px-2 text-sm text-gray-600 dark:text-white"
                    onClick={onGetDropper}
                  >
                    <PipetteIcon className="size-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent align="start" sideOffset={13}>
                  Eyedropper
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <div className="h-6 border-l" />
          <TooltipProvider>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <button
                  className="px-2 text-sm text-gray-600 underline dark:text-gray-300"
                  onClick={_onDefault}
                >
                  <RotateCcwIcon className="size-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent align="start" sideOffset={13}>
                Reset
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      <PopoverContent
        align="start"
        side="bottom"
        className="z-[9999999] p-0 w-auto"
      >
        <div className="dark:text-gray-900">
          <SketchPicker
            disableAlpha
            color={curColor || "#fff"}
            onChange={(color) => setCurColor(color.hex)}
            className="!shadow-lg !rounded-lg dark:text-gray-900"
            presetColors={swatches}
            onChangeComplete={(e) => {
              onChange(e.hex);
            }}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ColorPicker;
